import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { mapIntegrationProviderFromDTO } from '@/modules/common/mappers/integration.mapper';
import { IntegrationProviderDTO } from '@/modules/common/types/dto/response/integration-response';
import { getIntegrationDisplayName } from '@/modules/common/utils/integration-icons';
import { formatMinutesToTimeString, formatWallClockDate } from '@/utils/date-formatters';
import { logger } from '@/utils/logger/logger';

import { NOTIFICATION_TYPE_CONFIG } from '../../constants/notification-type-config';
import { Notification } from '../../types';
import { NotificationItemShell } from './notification-item-shell';

const TAG = 'NotificationItemWorklogList:';

interface WorklogListEntry {
  id: number;
  date: string;
  trackedMinutes: number;
  /** Only sent to managers — their notification covers other people's worklogs, so each needs a name. */
  userName?: string;
}

interface WorklogListProjectGroup {
  projectId: number;
  projectName: string;
  worklogs: WorklogListEntry[];
}

interface NotificationItemWorklogListProps {
  notification: Notification;
  onMarkAsRead?: (notificationId: number) => void;
  onDelete?: (notificationId: number) => void;
}

const parseWorklogGroups = (worklogsJson?: string): WorklogListProjectGroup[] => {
  if (!worklogsJson) return [];
  try {
    return JSON.parse(worklogsJson) as WorklogListProjectGroup[];
  } catch (error) {
    logger.warn(TAG, 'Failed to parse worklogsJson', error);
    return [];
  }
};

/** `args.integration` is the raw wire provider value (e.g. "JIRA"); falls back to an empty name if absent. */
const resolveIntegrationName = (integration?: string): string => {
  if (!integration) return '';
  return getIntegrationDisplayName(
    mapIntegrationProviderFromDTO(integration as IntegrationProviderDTO)
  );
};

/**
 * Inbox item for worklog notifications that carry a list of affected worklogs, grouped by project
 * (currently just sync conflicts on approved worklogs).
 *
 * These lists are unbounded, so the backend sends them grouped by project as a JSON
 * blob (`args.worklogsJson`) instead of flat i18n args; this component parses and
 * renders it, taking its title/description from `NOTIFICATION_TYPE_CONFIG` so a
 * single renderer covers every notification type shaped this way.
 */
export function NotificationItemWorklogList({
  notification,
  onMarkAsRead,
  onDelete,
}: NotificationItemWorklogListProps) {
  const { t } = useTranslation('authenticated.notifications');

  const typeConfig = NOTIFICATION_TYPE_CONFIG[notification.type];
  const actionUrl = typeConfig?.getActionUrl
    ? typeConfig.getActionUrl(notification.actionArgs ?? {})
    : null;
  const integrationName = resolveIntegrationName(notification.args?.integration);
  const title = typeConfig ? t(typeConfig.titleKey) : '';
  const description = typeConfig
    ? t(typeConfig.descriptionKey, { integration: integrationName })
    : '';

  const groups = useMemo(
    () => parseWorklogGroups(notification.args?.worklogsJson),
    [notification.args?.worklogsJson]
  );

  return (
    <NotificationItemShell
      notification={notification}
      title={title}
      actionUrl={actionUrl}
      onMarkAsRead={onMarkAsRead}
      onDelete={onDelete}
    >
      <p className="text-sm leading-snug text-muted-foreground">{description}</p>
      <div className="flex flex-col gap-2">
        {groups.map(group => (
          <div key={group.projectId}>
            <p className="text-xs font-medium text-foreground">{group.projectName}</p>
            <ul className="mt-0.5 flex flex-col gap-0.5 pl-3">
              {group.worklogs.map(worklog => (
                <li key={worklog.id} className="text-xs text-muted-foreground">
                  {formatWallClockDate(worklog.date)} •{' '}
                  {formatMinutesToTimeString(worklog.trackedMinutes)}
                  {worklog.userName ? ` • ${worklog.userName}` : ''}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </NotificationItemShell>
  );
}
